import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client.js';

interface AdminUserRow {
  id: string;
  email: string;
  name: string | null;
  role: 'admin' | 'member' | 'viewer';
  status: 'active' | 'suspended' | 'deactivated' | 'invited';
  totp_enabled: boolean;
  google_sub: string | null;
  created_at: string;
}

const ROLES: AdminUserRow['role'][] = ['admin', 'member', 'viewer'];
const STATUSES: AdminUserRow['status'][] = ['active', 'suspended', 'deactivated', 'invited'];

export function AdminUsersPage(): JSX.Element {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'users'],
    // Server returns `{ users: [...] }`; normalize to `{ data }` so the rest
    // of this component (table, mutations, etc.) doesn't need to care.
    queryFn: async () => {
      const resp = (await api.get('/v1/admin/users')) as { users: AdminUserRow[] };
      return { data: resp.users };
    },
  });
  const patch = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, string> }) =>
      api.patch(`/v1/admin/users/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
  const [busyId, setBusyId] = useState<string | null>(null);

  if (isLoading) return <p className="text-sm text-ink-muted">Loading…</p>;
  if (error) return <p className="text-sm text-danger">{(error as Error).message}</p>;
  const rows = data?.data ?? [];

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-semibold">Users</h1>
        <p className="text-sm text-ink-muted">Class-of-service: change role and status.</p>
      </header>
      <div className="rounded border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-surface-alt text-left text-xs text-ink-muted">
            <tr>
              <th className="px-3 py-2">Email</th>
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">Role</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">2FA</th>
              <th className="px-3 py-2">SSO</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.id} className="border-t border-border">
                <td className="px-3 py-2 font-mono text-xs">{u.email}</td>
                <td className="px-3 py-2">{u.name ?? '—'}</td>
                <td className="px-3 py-2">
                  <select
                    value={u.role}
                    disabled={busyId === u.id}
                    onChange={(e) => {
                      setBusyId(u.id);
                      patch.mutate({ id: u.id, body: { role: e.target.value } }, {
                        onSettled: () => setBusyId(null),
                      });
                    }}
                    className="rounded border border-border bg-surface px-2 py-1 text-xs"
                  >
                    {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </td>
                <td className="px-3 py-2">
                  <select
                    value={u.status}
                    disabled={busyId === u.id}
                    onChange={(e) => {
                      setBusyId(u.id);
                      patch.mutate({ id: u.id, body: { status: e.target.value } }, {
                        onSettled: () => setBusyId(null),
                      });
                    }}
                    className="rounded border border-border bg-surface px-2 py-1 text-xs"
                  >
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td className="px-3 py-2 text-xs">
                  {u.totp_enabled
                    ? <span className="text-success">Enabled</span>
                    : <span className="text-ink-muted">—</span>}
                </td>
                <td className="px-3 py-2 text-xs">
                  {u.google_sub ? <span className="text-accent">Google</span> : <span className="text-ink-muted">—</span>}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-ink-muted text-sm">No users yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
